import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { FormBuilder } from '@/features/database/form/form-builder'
import { BoardView } from '@/features/database/board/board-view'
import { GalleryView } from '@/features/database/gallery/gallery-view'
import { GanttView } from '@/features/database/gantt/gantt-view'
import { ListView } from '@/features/database/list/list-view'
import { ScheduleView } from '@/features/database/schedule/schedule-view'
import { GridView } from '@/features/database/grid/grid-view'
import { rowHeightFor, type GridFieldOptions } from '@/features/database/grid/columns'
import { readFieldOptions } from '@/features/database/table/field-options'
import type { FieldPlacement, RowsView } from '@/features/database/views/field-order'
import { useFieldOptions, useFields, useViews } from '@/features/database/table/queries'
import { resolveView } from '@/features/database/table/selection'
import { ViewBar } from '@/features/database/table/view-bar'
import { PageBar } from '@/app/layout/page-bar'
import { useRealtimePage } from '@/features/realtime/use-realtime-page'
import { settingsQuery } from '@/features/settings/queries'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

const ROUTE = '/shell/database/$databaseId/table/$tableId'

/**
 * A table, shown through one of its views.
 *
 * The view is in the address, so a link to a filtered, sorted view of a table is
 * a link somebody can send. A url that names one is believed absolutely -- see
 * `resolveView` -- and a url that names none shows the first, which is what the
 * sidebar's table link means.
 */
export function TablePage() {
  const { databaseId, tableId } = useParams({ from: ROUTE })
  const {
    view: routeViewId,
    row: routeRowId,
    search: routeSearch,
  } = useSearch({ from: ROUTE })
  const navigate = useNavigate()
  const views = useViews(tableId)
  const fields = useFields(tableId)
  // Every event about this table's rows, fields and views is broadcast to the
  // table's own group. Without this the handlers registered in M4 and M6 are
  // correct and never called.
  useRealtimePage('table', { table_id: tableId })

  if (views.isPending || fields.isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PageBar />
        <GridSkeleton />
      </div>
    )
  }

  const all = views.data ?? []
  const view = resolveView(all, routeViewId)

  const bar = (
    <ViewBar
      views={all}
      view={view}
      fields={fields.data ?? []}
      tableId={tableId}
      databaseId={databaseId}
      search={routeSearch ?? ''}
      onSearch={(term) => {
        void navigate({
          to: '/database/$databaseId/table/$tableId',
          params: { databaseId, tableId },
          // The row is dropped along with everything else: a search changes
          // which rows there are, and a dialog left open over a row the view
          // no longer contains is a dialog about nothing.
          // The empty term is not special-cased here: the route's own
          // validator drops it, which is the one place that decides what
          // `?search=` means. Deciding it twice is two rules to keep in step.
          search: { view: routeViewId, search: term },
          // Replaced rather than pushed. Each pause in typing writes the
          // address, and a back button that walked through every prefix of a
          // word would take five presses to undo one search.
          replace: true,
        })
      }}
    />
  )

  if (view === undefined) {
    // The bar is here even with no views at all, because the way to make one is
    // in it. Hiding it left "this table has no views" as a message with no way
    // out of it.
    return <Frame bar={bar}>{all.length === 0 ? <NoViews /> : <ViewNotFound />}</Frame>
  }

  if (view.type === 'form') {
    return (
      <Frame bar={bar}>
        {/* Keyed like the grid, so nothing typed into one form's heading is
            still in the box when another form is shown. */}
        <FormBuilder
          key={view.id}
          view={view}
          fields={fields.data ?? []}
          tableId={tableId}
        />
      </Frame>
    )
  }

  return (
    <Frame bar={bar}>
      {/* Keyed on the view so the rows -- and the row window inside them -- are
          rebuilt from nothing when the view changes, rather than inheriting a
          scroll position measured against a different set of rows. */}
      <RowsView
        key={view.id}
        view={view}
        fields={fields.data ?? []}
        databaseId={databaseId}
        tableId={tableId}
        openRowId={routeRowId ?? null}
        search={routeSearch ?? ''}
      />
    </Frame>
  )
}

/** The view bar above whatever is being shown, and the room for it. */
function Frame({ bar, children }: { bar: React.ReactNode; children: React.ReactNode }) {
  const { t } = useTranslation()
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {bar !== null && <PageBar label={t('table.views')}>{bar}</PageBar>}
      {/* Clipped rather than scrolled: whatever is drawing the rows scrolls
          them itself, and a container that scrolled as well would put a second
          pair of bars around the first. */}
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{children}</div>
    </div>
  )
}

/**
 * The rows of a view, however that view draws them.
 *
 * The field options, the search mode and what opening a row does to the address
 * are the same question for every view type, so they are answered once here and
 * the type only decides which component gets the answers.
 */
function RowsView({
  view,
  fields,
  databaseId,
  tableId,
  openRowId,
  search,
}: {
  view: RowsView
  fields: readonly Field[]
  databaseId: number
  tableId: number
  openRowId: number | null
  search: string
}) {
  const options = useFieldOptions(view.id)
  const settings = useQuery(settingsQuery())
  const navigate = useNavigate()

  if (options.isPending) return <GridSkeleton />

  const rowSearch = {
    term: search,
    // Which search implementation this deployment has, which is a property of
    // the instance rather than of the reader: the two modes genuinely return
    // different rows, so it belongs in the query beside the term.
    mode:
      settings.data?.frontend.pg_fulltext_search_enabled === true
        ? ('full-text-with-count' as const)
        : ('compat' as const),
  }

  const onOpenRow = (rowId: number | null) => {
    // Replaced rather than pushed while stepping from one row to the next: the
    // back button should close the dialog, not walk back through every row
    // somebody looked at on the way.
    void navigate({
      to: '/database/$databaseId/table/$tableId',
      params: { databaseId, tableId },
      search: {
        view: view.id,
        row: rowId ?? undefined,
        // Carried rather than dropped: the row was reached through the search,
        // and losing it on the way into the dialog would change which rows the
        // previous and next buttons step through.
        search,
      },
      replace: openRowId !== null && rowId !== null,
    })
  }

  if (view.type === 'gantt') {
    return (
      <GanttView
        view={view}
        fields={fields}
        fieldOptions={readFieldOptions<FieldPlacement>(options.data)}
        openRowId={openRowId}
        onOpenRow={onOpenRow}
        search={rowSearch}
      />
    )
  }

  if (view.type === 'schedule') {
    return (
      <ScheduleView
        view={view}
        fields={fields}
        fieldOptions={readFieldOptions<FieldPlacement>(options.data)}
        openRowId={openRowId}
        onOpenRow={onOpenRow}
        search={rowSearch}
      />
    )
  }

  if (view.type === 'list') {
    return (
      <ListView
        view={view}
        fields={fields}
        fieldOptions={readFieldOptions<FieldPlacement>(options.data)}
        openRowId={openRowId}
        onOpenRow={onOpenRow}
        search={rowSearch}
      />
    )
  }

  if (view.type === 'board') {
    return (
      <BoardView
        view={view}
        fields={fields}
        fieldOptions={readFieldOptions<FieldPlacement>(options.data)}
        openRowId={openRowId}
        onOpenRow={onOpenRow}
        search={rowSearch}
      />
    )
  }

  if (view.type === 'gallery') {
    return (
      <GalleryView
        view={view}
        fields={fields}
        fieldOptions={readFieldOptions<FieldPlacement>(options.data)}
        openRowId={openRowId}
        onOpenRow={onOpenRow}
        search={rowSearch}
      />
    )
  }

  return (
    <GridView
      view={view}
      fields={fields}
      fieldOptions={readFieldOptions<GridFieldOptions>(options.data)}
      rowHeight={rowHeightFor(view.row_height_size)}
      openRowId={openRowId}
      onOpenRow={onOpenRow}
      search={rowSearch}
    />
  )
}

function GridSkeleton() {
  return (
    <div className="space-y-2 p-4">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

function NoViews() {
  const { t } = useTranslation()
  return (
    <Empty className="p-6">
      <EmptyHeader>
        <EmptyTitle>{t('table.noViews')}</EmptyTitle>
        <EmptyDescription>{t('table.noViewsDescription')}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  )
}

function ViewNotFound() {
  const { t } = useTranslation()
  return (
    <Empty className="p-6">
      <EmptyHeader>
        <EmptyTitle>{t('table.viewNotFoundTitle')}</EmptyTitle>
        <EmptyDescription>{t('table.viewNotFoundDescription')}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  )
}
